import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'account_id'
  const [formData, setFormData] = useState({
    email: '',
    account_id: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loginData = loginMethod === 'email' 
        ? { email: formData.email, password: formData.password }
        : { account_id: formData.account_id, password: formData.password };
      
      await login(loginData);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <h1 className="text-3xl font-bold text-white font-['Outfit']">Welcome back</h1>
            <p className="mt-2 text-zinc-400">Sign in to access your trading account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {/* Login Method Toggle */}
            <div className="flex rounded-lg bg-zinc-900/50 p-1">
              <button
                type="button"
                onClick={() => setLoginMethod('email')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMethod === 'email'
                    ? 'bg-primary text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('account_id')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMethod === 'account_id'
                    ? 'bg-primary text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Account ID
              </button>
            </div>

            <div className="space-y-4">
              {loginMethod === 'email' ? (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-zinc-900/50 border-zinc-800 text-white placeholder-zinc-500 focus:border-primary"
                    data-testid="login-email-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="account_id" className="text-zinc-300">Account ID</Label>
                  <Input
                    id="account_id"
                    type="text"
                    placeholder="1000001"
                    value={formData.account_id}
                    onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                    required
                    className="bg-zinc-900/50 border-zinc-800 text-white placeholder-zinc-500 focus:border-primary font-mono"
                    data-testid="login-account-id-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-zinc-300">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="bg-zinc-900/50 border-zinc-800 text-white placeholder-zinc-500 focus:border-primary pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full glow-primary"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              Sign In
            </Button>
          </form>

          <p className="text-center text-zinc-400">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] to-transparent z-10" />
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1639091824355-48221063de7f?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center p-12">
          <div className="glass-card p-8 max-w-md">
            <blockquote className="text-lg text-white italic mb-4">
              &ldquo;GeneiProFx transformed my trading. The tools are professional-grade and the execution is lightning fast.&rdquo;
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                JD
              </div>
              <div>
                <div className="font-medium text-white">John D.</div>
                <div className="text-sm text-zinc-400">Professional Trader</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
